import type {
    OptimizelyDataFile,
    UserInfo,
    OptimizelyProviderProps,
    NextProps
} from '../optimizely.types';

import { useEffect, useMemo } from 'react';
import { ReactSDKClient, createInstance } from '@optimizely/react-sdk';
import { v4 as uuidv4 } from 'uuid';

import {
    forceExperiments,
    OptimizelyOverrides,
    writeOverridesToCookies
} from '../optimizelyOverrides';
import { addNotificationForContentSquare } from '../optimizelyTrackContentSquare';
import { getOptimizelyUserInfo } from '../optimizely';
import { useUserSetOptimizelyOverrides } from './useUserSetOptimizelyOverrides';
import { getAnonId } from '../getAnonIdFromContext';

type ExtendedReactSDKClient = ReactSDKClient & {
    getIdentifier?: () => string;
    sdkKey?: string;
};

const halfASecInMs = 500;
/** Client Only Optimizely ReactSDKClient cache */
let optimizelyClient: ExtendedReactSDKClient | undefined;

export const clearClientCache = () => {
    optimizelyClient = undefined;
};

/**
 * Wrap the official createInstance method with pre-baked config and debug ids
 */
export const createOptimizelyReactSDKClient = (
    datafile: OptimizelyDataFile,
    sdkKey?: string
) => {
    const uuid = uuidv4();
    const client: ExtendedReactSDKClient = createInstance({
        datafile,
        eventBatchSize: 10,
        eventFlushInterval: 1000,
        sdkKey
    });
    client.getIdentifier = () => uuid;
    client.sdkKey = sdkKey;
    return client;
};

/**
 * Return a fresh ReactSDKClient if on the server or reuse exiting on client
 */
const initialiseOptimizely = (
    datafile: OptimizelyDataFile,
    isStatic: boolean
): ExtendedReactSDKClient => {
    const isServerSide = typeof window === 'undefined';
    const _client =
        optimizelyClient ??
        createOptimizelyReactSDKClient(
            datafile,
            // enable datafile re-fetching for static pages as we serve from cache and it may be out of date
            isStatic ? process.env['NEXT_PUBLIC_OPTIMIZELY_KEY'] : undefined
        );

    // For SSG and SSR always create a new client, so exit before mem-caching
    if (isServerSide) return _client;
    // write SDKClient to cache for re-use on client. Ignore isStatic clients as they have datafile re-fetching enabled
    if (!optimizelyClient && !isStatic) optimizelyClient = _client;
    return _client;
};

export enum WITH_OPTIMIZELY_PAGE_PROP_NAME {
    DATEFILE = '__OPTIMIZELY_DATAFILE__',
    USER = '__OPTIMIZELY_USER__',
    STATIC_RENDER = '__OPTIMIZELY_IS_STATIC__',
    OVERRIDES = '__OPTIMIZELY_OVERRIDES__'
}

export type WithOptimizelyPageProps = {
    [WITH_OPTIMIZELY_PAGE_PROP_NAME.USER]?: UserInfo;
    [WITH_OPTIMIZELY_PAGE_PROP_NAME.DATEFILE]?: OptimizelyDataFile;
    [WITH_OPTIMIZELY_PAGE_PROP_NAME.OVERRIDES]?: OptimizelyOverrides;
    [WITH_OPTIMIZELY_PAGE_PROP_NAME.STATIC_RENDER]?: boolean;
};

type PageProps = Record<string, unknown> & WithOptimizelyPageProps;

/**
 * We still have some pages that use getInitialProps() so this can execute on
 * the client
 */
const useOptimizelyProviderProps = (pageProps: PageProps) => {
    const isServerSide = typeof window === 'undefined';
    const documentCookieAnonId = getAnonId({});
    const windowUserAgent =
        (!isServerSide && window?.navigator.userAgent) || '';
    const userSetOverrides = useUserSetOptimizelyOverrides();

    const datafile = pageProps[WITH_OPTIMIZELY_PAGE_PROP_NAME.DATEFILE];
    const isStatic = !!pageProps[WITH_OPTIMIZELY_PAGE_PROP_NAME.STATIC_RENDER];
    const optimizelyOverrides: OptimizelyOverrides | undefined =
        pageProps[WITH_OPTIMIZELY_PAGE_PROP_NAME.OVERRIDES];
    let user: UserInfo | undefined =
        pageProps[WITH_OPTIMIZELY_PAGE_PROP_NAME.USER];

    if (!datafile)
        console.error('OptimizelyClient requires a datafile to be provided');

    /** Create Optimizely client */
    const optimizely = useMemo(
        () => initialiseOptimizely(datafile, isStatic),
        [datafile, isStatic]
    );

    /** Resolve user on client if not provided in pageProps */
    if (!user?.id) {
        if (documentCookieAnonId) {
            user = getOptimizelyUserInfo(documentCookieAnonId, windowUserAgent);
            optimizely.setUser(user); // should we always do this? its required for getEnabledFeatures to work correctly
        }
    }

    /** Lazy write overrides to cookies & SDK */
    useEffect(() => {
        if (userSetOverrides) {
            writeOverridesToCookies(userSetOverrides);
            if (userSetOverrides.experiments) {
                forceExperiments(
                    userSetOverrides.experiments,
                    documentCookieAnonId,
                    optimizely
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSetOverrides]);

    /** NewRelic custom attributes to aid debugging */
    useEffect(() => {
        /* note: this may not truly reflect the state of the page as user overrides will impact the state of flags, however user overrides are an internal tool and so this should not cause any widespread issues */
        const enabledFeatures = optimizely?.getEnabledFeatures(
            user?.id ?? undefined,
            user?.attributes
        );
        if (enabledFeatures?.length) {
            window.newrelic?.setCustomAttribute(
                'enabledOptimizelyFeatures',
                enabledFeatures.join('|')
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        addNotificationForContentSquare(optimizely);
    }, [optimizely]);

    const optimizelyProviderProps: OptimizelyProviderProps = {
        timeout: halfASecInMs,
        user,
        isServerSide,
        optimizely
    };

    return {
        optimizelyProviderProps,
        optimizelyOverrides
    };
};

export default useOptimizelyProviderProps;

export type OptimizelyProps = {
    datafile?: OptimizelyDataFile;
    user?: UserInfo;
    optimizelyOverrides?: OptimizelyOverrides;
    isStatic?: boolean;
};

// https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#distributive-conditional-types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DistributeWithOptimizelyPageProps<T> = T extends any
    ? T & WithOptimizelyPageProps
    : never;

/**
 * Decorate pageProps within data fetching methods to support _app level
 * useOptimizelyProviderProps usage and thus optimizely provider
 */
export const addOptimizelyProps = <P extends NextProps = NextProps>(
    { datafile, user, optimizelyOverrides, isStatic = false }: OptimizelyProps,
    pageProps: P
) => {
    const dupe = { ...pageProps } as DistributeWithOptimizelyPageProps<P>;
    if (datafile) dupe[WITH_OPTIMIZELY_PAGE_PROP_NAME.DATEFILE] = datafile;
    if (user) dupe[WITH_OPTIMIZELY_PAGE_PROP_NAME.USER] = user;
    if (optimizelyOverrides)
        dupe[WITH_OPTIMIZELY_PAGE_PROP_NAME.OVERRIDES] = optimizelyOverrides;
    dupe[WITH_OPTIMIZELY_PAGE_PROP_NAME.STATIC_RENDER] = isStatic;
    return dupe;
};
